import { useLocation, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/useUser";
import PageLoadingFallback from "@/components/layout/PageLoadingFallback";

/**
 * App-wide access gate. The product has no free plan, so a logged-in user
 * without an active/trialing subscription (and who isn't a super-admin) may not
 * reach ANY page — public marketing pages included — until they pick a plan.
 *
 * Wraps the whole route tree (above <Routes>), so it covers public routes too,
 * unlike ProtectedRoute which only guards individual protected routes.
 * Server-side report-generation enforcement still lives in _shared/quota.ts.
 *
 * Non-authenticated visitors are unaffected — they browse marketing freely.
 */

// Pages a logged-in, not-yet-subscribed user must still be able to reach.
const ALLOWLIST = new Set<string>([
  "/choose-plan",
  "/auth",
  "/reset-password",
  // Email unsubscribe must work regardless of plan state (CAN-SPAM/GDPR).
  "/unsubscribe",
  // Legal/policy pages must stay reachable regardless of plan state — a lapsed
  // customer needs to read the terms governing their own account.
  "/privacy",
  "/terms",
  "/dpa",
  "/ai-disclaimer",
]);

// Prefixes for link-addressed pages that must stay reachable. Kept separate
// from ALLOWLIST because the paths carry a dynamic segment.
// "/report/" (share links) — note the trailing slash keeps the gated
// "/report" generator page and "/report-library" behind the gate.
const ALLOWLIST_PREFIXES = ["/report/"];

export function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, isLoading: authLoading } = useUser();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["subscription-access", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status, is_super_admin")
        .eq("id", user!.id)
        .maybeSingle();
      return data ?? null;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Always allow the pages needed to authenticate / choose a plan, plus the
  // link-addressed pages recipients reach from outside the app.
  if (
    ALLOWLIST.has(location.pathname) ||
    ALLOWLIST_PREFIXES.some((p) => location.pathname.startsWith(p))
  ) {
    return <>{children}</>;
  }

  // Auth state still resolving — hold so a gated user never flashes app content.
  if (authLoading) return <PageLoadingFallback />;

  // Not logged in → public/marketing pages render normally.
  if (!user) return <>{children}</>;

  // Logged in → wait for subscription status, then gate.
  if (profileLoading) return <PageLoadingFallback />;

  const status = profile?.subscription_status;
  const hasAccess =
    profile?.is_super_admin === true || status === "active" || status === "trialing";

  if (!hasAccess) return <Navigate to="/choose-plan" replace />;

  return <>{children}</>;
}

export default SubscriptionGate;
